/**
 * Main routing configuration with deep hierarchical linking
 *
 * Approach: URL-driven state synchronization
 * - URL changes → Update app state (view, selectedEntity, etc.)
 * - User interactions → Navigate to new URL → State updates
 *
 * Routes structure:
 * - / → /home
 * - /home → Home view
 * - /today → Today timeline
 * - /projects → Projects list
 * - /projects/:id → Project detail
 * - /projects/:id/tasks/:taskId → Specific task in project
 * - /calendar → Calendar view
 * - /contacts → Contacts view
 * - /contacts/:id → Contact detail
 * - /accounting/invoices/:id → Invoice detail
 */

import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import AppNew from './AppNew';

import { AdminAIView } from './views/AdminAIView';
import { GoogleOAuthCallback } from './pages/GoogleOAuthCallback';
import {
  MicrosoftOAuthCallback,
  DropboxOAuthCallback,
  SlackOAuthCallback,
  NotionOAuthCallback,
} from './pages/OAuthCallback';
import { TrueLayerOAuthCallback } from './pages/TrueLayerOAuthCallback';
import { LoginPage } from './components/LoginPage';
import { RegisterPage } from './components/RegisterPage';
import { OwnerRegistrationPage } from './components/auth/OwnerRegistrationPage';
import { InvitationAcceptPage } from './components/auth/InvitationAcceptPage';
import { GuestMeetingPage } from './components/videocall/GuestMeetingPage';
import { ProtectedRoute, AuthRoute } from './components/auth';
import { AuthProvider } from './contexts/AuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { QueryProvider } from './providers/QueryProvider';
import { useAppStore } from '@business-assistant/shared';
import { useAppInit } from './hooks/useAppInit';
import { useWorkflowEngine } from './hooks/useWorkflowEngine';
import './components/AuthPages.css';

/**
 * App Initializer - loads data and seeds mock data if needed
 */
function AppInitializer({ children }: { children: React.ReactNode }) {
  const { loading, error, initialized } = useAppInit();

  // Initialize workflow engine (notifications, polling, AI classification)
  useWorkflowEngine();

  if (loading || !initialized) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: '1rem',
        backgroundColor: 'var(--bg-primary, #ffffff)',
      }}>
        {error ? (
          <>
            <h2 style={{ color: 'var(--text-error, #ef4444)' }}>Initialization Error</h2>
            <p style={{ color: 'var(--text-muted, #6b7280)' }}>{error}</p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--primary, #3b82f6)',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </>
        ) : (
          <>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid var(--border, #e5e7eb)',
              borderTopColor: 'var(--primary, #3b82f6)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
            <p style={{ color: 'var(--text-muted, #6b7280)' }}>Loading...</p>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </>
        )}
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * URL Synchronizer - reads URL and updates app state
 */
function URLSync({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const params = useParams();
  const { setCurrentView, selectEntity, setActiveFilter } = useAppStore();

  useEffect(() => {
    const path = location.pathname;

    // Map URL to view
    if (path.startsWith('/home')) {
      setCurrentView('home');
    } else if (path.startsWith('/today')) {
      setCurrentView('today');
      // Entity selection: /today/:entityType/:entityId
      if (params.entityType && params.entityId) {
        selectEntity(params.entityId);
        setActiveFilter(params.entityType);
      }
    } else if (path.startsWith('/projects')) {
      setCurrentView('projects');
      // Project selection: /projects/:id
      if (params.id) {
        selectEntity(params.id);
      }
      // Section selection: /projects/:id/:section
      if (params.section) {
        setActiveFilter(`projects:${params.section}`);
      }
      // Entity selection: /projects/:id/:section/:entityId (widget open)
      if (params.entityId) {
        selectEntity(params.entityId);
      }
    } else if (path.startsWith('/calendar')) {
      setCurrentView('calendar');
    } else if (path.startsWith('/contacts')) {
      setCurrentView('hub');
      // Contact selection: /contacts/:id
      if (params.id) {
        selectEntity(params.id);
      }
    } else if (path.startsWith('/accounting')) {
      setCurrentView('accounting');
      // Invoice selection: /accounting/invoices/:id
      if (params.id && path.includes('invoices')) {
        selectEntity(params.id);
        setActiveFilter('invoices');
      } else if (path.includes('/expenses')) {
        setActiveFilter('expenses');
      } else if (path.includes('/forecast')) {
        setActiveFilter('forecast');
      }
    } else if (path.startsWith('/settings')) {
      setCurrentView('settings');
      // Settings section: /settings/:section
      if (params.section) {
        setActiveFilter(params.section);
      }
    } else if (path.startsWith('/admin-ai')) {
      // Admin AI console - doesn't set a view as it has its own layout
      // View state not needed as AdminAIView handles its own routing
    }
  }, [location, params, setCurrentView, selectEntity, setActiveFilter]);

  return <>{children}</>;
}

/**
 * Login page wrapper with navigation handlers
 */
function LoginPageWrapper() {
  const navigate = useNavigate();

  return (
    <LoginPage
      onLoginSuccess={() => navigate('/home')}
      onRegisterClick={() => navigate('/register')}
      onForgotPasswordClick={() => {/* TODO: implement forgot password */}}
    />
  );
}

/**
 * Register page wrapper with navigation handlers
 */
function RegisterPageWrapper() {
  const navigate = useNavigate();

  return (
    <RegisterPage
      onRegisterSuccess={() => navigate('/login')}
      onLoginClick={() => navigate('/login')}
    />
  );
}

/**
 * Main router component
 */
export function AppRouter() {
  return (
    <BrowserRouter>
      <QueryProvider>
      <AuthProvider>
        <PermissionsProvider>
        <Routes>
          {/* Auth routes - only for non-authenticated users */}
          <Route
            path="/login"
            element={
              <AuthRoute>
                <LoginPageWrapper />
              </AuthRoute>
            }
          />
          <Route
            path="/register"
            element={
              <AuthRoute>
                <RegisterPageWrapper />
              </AuthRoute>
            }
          />

          {/* Owner Registration - for new organizations */}
          <Route
            path="/register-owner"
            element={
              <AuthRoute>
                <OwnerRegistrationPage />
              </AuthRoute>
            }
          />

          {/* Invitation Accept - public route to accept team invitations */}
          <Route
            path="/invite/:token"
            element={<InvitationAcceptPage />}
          />

          {/* Protected routes - require authentication */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppInitializer>
                  <URLSync>
                    <AppNew />
                  </URLSync>
                </AppInitializer>
              </ProtectedRoute>
            }
          >
            {/* Root redirect */}
            <Route index element={<Navigate to="/home" replace />} />

            {/* Main views - all render AppNew with different state */}
            <Route path="home" element={null} />
            <Route path="today" element={null} />
            <Route path="today/:entityType/:entityId" element={null} />

            {/* Projects - Full deep linking support */}
            <Route path="projects" element={null} />
            <Route path="projects/:id" element={null} />
            <Route path="projects/:id/:section" element={null} />
            <Route path="projects/:id/:section/:entityId" element={null} />

            {/* Calendar */}
            <Route path="calendar" element={null} />

            {/* Contacts */}
            <Route path="contacts" element={null} />
            <Route path="contacts/:id" element={null} />
            <Route path="contacts/:id/conversations" element={null} />

            {/* Accounting */}
            <Route path="accounting" element={null} />
            <Route path="accounting/invoices" element={null} />
            <Route path="accounting/invoices/:id" element={null} />
            <Route path="accounting/expenses" element={null} />
            <Route path="accounting/forecast" element={null} />

            {/* Settings */}
            <Route path="settings" element={null} />
            <Route path="settings/:section" element={null} />

            {/* Admin AI Console (admin only - hidden from regular users) */}
            <Route path="admin-ai" element={<AdminAIView />} />
            <Route path="admin-ai/:section" element={<AdminAIView />} />

            {/* Video Call */}
            <Route path="hub/videocall/:roomId" element={null} />

            {/* Test view (outside AppNew) */}
            

            {/* Fallback for authenticated users */}
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Route>

          {/* Guest meeting page (outside auth protection) */}
          <Route path="/meet/:roomSlug" element={<GuestMeetingPage />} />

          {/* OAuth callback routes (outside main app wrapper and auth protection) */}
          <Route path="/oauth/google/callback" element={<GoogleOAuthCallback />} />
          <Route path="/oauth/microsoft/callback" element={<MicrosoftOAuthCallback />} />
          <Route path="/oauth/dropbox/callback" element={<DropboxOAuthCallback />} />
          <Route path="/oauth/slack/callback" element={<SlackOAuthCallback />} />
          <Route path="/oauth/notion/callback" element={<NotionOAuthCallback />} />
          <Route path="/oauth/truelayer/callback" element={<TrueLayerOAuthCallback />} />
        </Routes>
        </PermissionsProvider>
      </AuthProvider>
      </QueryProvider>
    </BrowserRouter>
  );
}

// Re-export from hooks for backward compatibility
// NOTE: useAppNavigate is now in hooks/useAppNavigate.ts to avoid circular dependencies
export { useAppNavigate, type ProjectSection } from './hooks/useAppNavigate';
